import { AppSettings, UserPreferences } from '@/types/index';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:8000');
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;

// Azure credentials are NO LONGER NEEDED on frontend
// Voice synthesis is handled completely by backend via WebSocket
// These are kept as optional for backward compatibility
const AZURE_SPEECH_KEY = import.meta.env.VITE_AZURE_SPEECH_KEY || '';
const AZURE_SPEECH_REGION = import.meta.env.VITE_AZURE_SPEECH_REGION || '';

const LLM_PROVIDER = import.meta.env.VITE_LLM_PROVIDER || 'openai';
const LLM_API_KEY = import.meta.env.VITE_LLM_API_KEY;
const LLM_MODEL = import.meta.env.VITE_LLM_MODEL || 'gpt-3.5-turbo';
const LLM_TEMPERATURE = Number(import.meta.env.VITE_LLM_TEMPERATURE) || 0.7;

const ACS_ENABLED = import.meta.env.VITE_ACS_ENABLED === 'true';
const ACS_ENDPOINT = import.meta.env.VITE_ACS_ENDPOINT || '';

const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';
const DEBUG_MODE = import.meta.env.VITE_DEBUG === 'true';

export const config = {
  app: {
    name: import.meta.env.VITE_APP_NAME || 'Aahana SmartTutor',
    version: '1.0.0',
    env: APP_ENV as 'development' | 'staging' | 'production',
    debugMode: DEBUG_MODE,
  },
  api: {
    baseURL: API_BASE_URL,
    apiVersion: API_VERSION,
    timeout: API_TIMEOUT,
    retries: 3,
    retryDelay: 1000,
  },
  azure: {
    speechKey: AZURE_SPEECH_KEY,
    region: AZURE_SPEECH_REGION,
    recognitionLanguage: 'en-US',
    synthesisLanguage: 'en-US',
  },
  llm: {
    provider: LLM_PROVIDER,
    apiKey: LLM_API_KEY,
    model: LLM_MODEL,
    temperature: LLM_TEMPERATURE,
    maxTokens: 2048,
  },
  acs: {
    enabled: ACS_ENABLED,
    endpoint: ACS_ENDPOINT,
  },
  voice: {
    language: 'en-US',
    rate: 1.0,
    pitch: 1.0,
    volume: 1.0,
  },
  cache: {
    enabled: APP_ENV === 'production',
    ttl: 3600, // 1 hour
  },
};

export const defaultAppSettings: AppSettings = {
  apiTimeout: API_TIMEOUT,
  enableCache: APP_ENV === 'production',
  debugMode: DEBUG_MODE,
  environment: APP_ENV as 'development' | 'staging' | 'production',
};

export const defaultUserPreferences: UserPreferences = {
  theme: 'auto',
  language: 'en',
  notifications: true,
  voiceEnabled: true,
};

export const isProduction = APP_ENV === 'production';
export const isDevelopment = APP_ENV === 'development';

export const validateConfig = (): boolean => {
  const errors: string[] = [];

  if (!AZURE_SPEECH_KEY) {
    errors.push('VITE_AZURE_SPEECH_KEY is not configured');
  }
  if (!AZURE_SPEECH_REGION) {
    errors.push('VITE_AZURE_SPEECH_REGION is not configured');
  }
  if (!LLM_API_KEY) {
    errors.push('VITE_LLM_API_KEY is not configured');
  }

  if (errors.length > 0) {
    if (isDevelopment) {
      console.warn('Configuration warnings:', errors);
    }
  }

  return errors.length === 0;
};
